import {showNotification} from 'platform/components';
import {match} from 'ts-pattern';

import {isNil} from 'ramda';

import {
  ModuleSourceEnum,
  RenderedTemplateResponseBody,
  useRenderBalanceInvoiceDocumentMutation,
  useRenderCorrectiveTaxDocumentMutation,
  useRenderInvoiceDocumentMutation,
  useRenderInvoiceProformaDocumentMutation,
  useRenderTaxDocumentForPaymentDocumentMutation,
} from '@dms/api/finance';

import {Nullish} from 'shared/web';

import {InvoiceTypes} from '../types/InvoiceTypes';
import {ServiceCaseInvoiceType} from '../types/ServiceCaseInvoiceType';
import {downloadFile} from '../utils/downloadFile';
import {handleApiError} from '../utils/handleApiError';
import {printPDF} from '../utils/printPDF';
import {useDownloadServiceCaseInvoiceDocument} from './useDownloadServiceCaseInvoiceDocument';

// Documents issued from a service case are exported by a workshop endpoint that renders PDF only.
const SERVICE_CASE_MODULE_SOURCES: ModuleSourceEnum[] = ['service-case', 'service-direct'];

const isServiceCaseModuleSource = (moduleSource: ModuleSourceEnum | Nullish) =>
  !isNil(moduleSource) && SERVICE_CASE_MODULE_SOURCES.includes(moduleSource);

// Drafts are rendered by accounting whatever their module source.
export const hasAccountingDocumentDocxRendition = (
  invoiceType: InvoiceTypes | Nullish,
  moduleSource: ModuleSourceEnum | Nullish
) => invoiceType === 'DRAFT' || !isServiceCaseModuleSource(moduleSource);

export type DownloadAccountingDocument = ReturnType<typeof useDownloadAccountingDocument>[0];

/**
 * @param action `download` and `print` work with the rendered PDF, `downloadDocx` downloads the
 * original DOCX rendition. Printing a DOCX is not supported, therefore it is not representable.
 */
export const useDownloadAccountingDocument = (
  action: 'download' | 'print' | 'downloadDocx' = 'download'
) => {
  const [
    getProformaDocument,
    {isLoading: loadingProformaDocument, isError: isProformaDocumentError},
  ] = useRenderInvoiceProformaDocumentMutation();
  const [getInvoiceDocument, {isLoading: loadingInvoiceDocument, isError: isInvoiceDocumentError}] =
    useRenderInvoiceDocumentMutation();
  const [
    getBalanceInvoiceDocument,
    {isLoading: loadingBalanceInvoiceDocument, isError: isBalanceInvoiceDocumentError},
  ] = useRenderBalanceInvoiceDocumentMutation();
  const [
    getCorrectiveTaxDocument,
    {isLoading: loadingCorrectiveTaxDocument, isError: isCorrectiveTaxDocumentError},
  ] = useRenderCorrectiveTaxDocumentMutation();
  const [
    getTaxDocumentForPayment,
    {isLoading: loadingTaxDocumentForPayment, isError: isTaxDocumentForPaymentError},
  ] = useRenderTaxDocumentForPaymentDocumentMutation();

  const [
    downloadServiceCaseInvoiceDocument,
    {
      isLoading: isDownloadingServiceCaseInvoiceDocument,
      isError: isErrorServiceCaseInvoiceDocument,
    },
  ] = useDownloadServiceCaseInvoiceDocument(action === 'print' ? 'print' : 'download');

  const isLoading =
    loadingProformaDocument ||
    loadingInvoiceDocument ||
    loadingBalanceInvoiceDocument ||
    loadingCorrectiveTaxDocument ||
    loadingTaxDocumentForPayment;
  const isError =
    isProformaDocumentError ||
    isInvoiceDocumentError ||
    isBalanceInvoiceDocumentError ||
    isCorrectiveTaxDocumentError ||
    isTaxDocumentForPaymentError;

  const handleRenderedDocument = (renderedDocument: RenderedTemplateResponseBody) =>
    match(action)
      .with('download', () => downloadFile(renderedDocument.pdfUrl))
      .with('print', () => printPDF(renderedDocument.pdfUrl))
      .with('downloadDocx', () => downloadFile(renderedDocument.url, {fileType: 'attachment'}))
      .exhaustive();

  // eslint-disable-next-line max-params -- Pre-existing violation
  const downloadInvoice = async (
    invoiceType: InvoiceTypes | Nullish,
    invoiceId: string,
    moduleSource: ModuleSourceEnum | Nullish,
    invoiceNumber: string | Nullish,
    invoiceCategory?: 'default' | 'internal' | null,
    isMargin = false
  ) => {
    if (isNil(invoiceType)) {
      showNotification.error();
      return;
    }

    const downloadAction = match(invoiceType)
      .with('PROFORMA', () => getProformaDocument)
      .with('TAX_DOCUMENT_FOR_PAYMENT', () => getTaxDocumentForPayment)
      .with('BALANCE_INVOICE', () => getBalanceInvoiceDocument)
      .with('CORRECTIVE_TAX_DOCUMENT', () => getCorrectiveTaxDocument)
      .otherwise(() => getInvoiceDocument);

    const renderDocumentRequestBody = {
      documentId: invoiceId,
      isMarginPrint: isMargin,
    };

    const serviceCaseType = match<InvoiceTypes, ServiceCaseInvoiceType>(invoiceType)
      .with('PROFORMA', () => 'invoice-proforma')
      .with('TAX_DOCUMENT_FOR_PAYMENT', () => 'tax-document-for-payment')
      .with('CORRECTIVE_TAX_DOCUMENT', () =>
        invoiceCategory === 'internal'
          ? 'internal-corrective-tax-document'
          : 'corrective-tax-document'
      )
      .with('BALANCE_INVOICE', () => 'balance-invoice')
      .with('ISSUED', () => (invoiceCategory === 'internal' ? 'invoice-internal' : 'invoice'))
      .otherwise(() => 'invoice');

    const isServiceCaseDocument =
      invoiceType !== 'DRAFT' && isServiceCaseModuleSource(moduleSource);

    if (
      action === 'downloadDocx' &&
      !hasAccountingDocumentDocxRendition(invoiceType, moduleSource)
    ) {
      showNotification.error();
      return;
    }

    if (isServiceCaseDocument) {
      downloadServiceCaseInvoiceDocument(invoiceId, invoiceNumber, serviceCaseType);
    } else {
      await downloadAction({
        renderDocumentRequestBody,
      })
        .unwrap()
        .then(handleRenderedDocument)
        .catch(handleApiError);
    }
  };

  return [
    downloadInvoice,
    {
      isLoading: isLoading || isDownloadingServiceCaseInvoiceDocument,
      isError: isError || isErrorServiceCaseInvoiceDocument,
    },
  ] as const;
};
